import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { holidaysApi } from "@/lib/api/holidays";
import { performanceApi } from "@/lib/api/performance";
import { employeeLifecycleApi } from "@/lib/api/employeeLifecycle";
import { recruitmentApi } from "@/lib/api/recruitment";
import { employeesApi } from "@/lib/api/employees";
import { announcementsApi } from "@/lib/api/announcements";
import { qk } from "@/lib/query-client";
import { businessToday, toBusinessDate } from "@/lib/utils/attendance-date";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { EmptyState } from "@/components/ui/EmptyState";
import { Skeleton } from "@/components/ui/Skeleton";
import { Icon } from "@/components/layout/Icon";
import { cn } from "@/lib/utils/cn";
import { formatDate } from "@/lib/utils/format";
import { can } from "@/stores/auth-store";
import { useFeatureEnabled } from "@/lib/features";

const WINDOW_DAYS = 30;
const MAX_ITEMS = 8;

interface UpcomingEvent {
  key: string;
  icon: string;
  title: string;
  detail: string;
  date: string; // "YYYY-MM-DD"
  tone: "indigo" | "emerald" | "amber" | "accent";
}

const TONE_CLASSES: Record<UpcomingEvent["tone"], string> = {
  indigo: "bg-indigo-50 text-indigo-600",
  emerald: "bg-emerald-50 text-emerald-600",
  amber: "bg-amber-50 text-amber-600",
  accent: "bg-accent-50 text-accent-700",
};

function todayISO(): string {
  return businessToday();
}

function addDaysISO(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return toBusinessDate(d);
}

/** "Today" / "Tomorrow" / "in 5 days" - a short relative badge, not a
 * replacement for the full formatted date shown alongside it. */
function relativeDay(dateISO: string): string {
  const diffDays = Math.round((new Date(dateISO + "T00:00:00").getTime() - new Date(todayISO() + "T00:00:00").getTime()) / 86400000);
  if (diffDays <= 0) return "Today";
  if (diffDays === 1) return "Tomorrow";
  return `in ${diffDays} days`;
}

interface FailedSource {
  key: string;
  refetch: () => void;
}

/** Compact, informational "what's coming next" list - birthdays,
 * anniversaries, holidays, interviews, performance deadlines, onboarding
 * start dates and company events published under Announcements, each
 * independently permission-gated and error-isolated the same way
 * ActionCenter is. The one action is "Add event" for announcement authors -
 * this is the "what's coming next" third of the dashboard's "what's
 * happening / what needs me / what's next" framing. */
export function UpcomingEvents({ className }: { className?: string } = {}) {
  const currentYear = new Date().getFullYear();
  const holidayEnabled = useFeatureEnabled("holiday_management");
  const recruitmentEnabled = useFeatureEnabled("recruitment");
  const performanceEnabled = useFeatureEnabled("performance_management");
  const employeeMgmtEnabled = useFeatureEnabled("employee_management");
  const announcementsEnabled = useFeatureEnabled("announcement_module");

  const canSeeHolidays = holidayEnabled && can("holiday.read");
  const canSeeInterviews = recruitmentEnabled && can("recruitment.interview.view");
  const canSeePerformance = performanceEnabled && can("performance.dashboard.view");
  const canSeeOnboarding = employeeMgmtEnabled && can("employee.lifecycle.read");
  const canSeeBirthdays = employeeMgmtEnabled && can("employee.read");
  // Events published under Announcements (type "event") are upcoming events
  // too; whoever can author them gets an "Add event" shortcut here.
  const canSeeCompanyEvents = announcementsEnabled && can("announcement.view");
  const canAddEvent = announcementsEnabled && can("announcement.manage");

  const holidaysQuery = useQuery({
    queryKey: qk.holidays.list(currentYear, { per_page: 100 }),
    queryFn: () => holidaysApi.list(currentYear, { per_page: 100 }),
    enabled: canSeeHolidays,
  });
  const interviewsQuery = useQuery({
    queryKey: qk.recruitment.interviews({ from: todayISO(), to: addDaysISO(14), per_page: 20 }),
    queryFn: () => recruitmentApi.listInterviews({ from: todayISO(), to: addDaysISO(14), per_page: 20 }),
    enabled: canSeeInterviews,
  });
  // Same query ActionCenter fires for pending review counts - dedupes by
  // key, not a second request when both sections are mounted together.
  const performanceQuery = useQuery({
    queryKey: qk.performance.dashboard,
    queryFn: performanceApi.dashboard,
    enabled: canSeePerformance,
  });
  const onboardingQuery = useQuery({
    queryKey: qk.onboarding.list({ status: "in_progress", per_page: 50 }),
    queryFn: () => employeeLifecycleApi.listOnboardings({ status: "in_progress", per_page: 50 }),
    enabled: canSeeOnboarding,
  });
  const birthdaysQuery = useQuery({
    queryKey: qk.employees.upcomingEvents({ within_days: WINDOW_DAYS }),
    queryFn: () => employeesApi.upcomingEvents({ within_days: WINDOW_DAYS }),
    enabled: canSeeBirthdays,
  });

  const companyEventsQuery = useQuery({
    queryKey: qk.announcements.list({ upcoming_events: true, type: "event", per_page: 50 }),
    queryFn: () => announcementsApi.list({ type: "event", per_page: 50 }),
    enabled: canSeeCompanyEvents,
  });

  const { events, failedSources } = useMemo(() => {
    const events: UpcomingEvent[] = [];
    const failedSources: FailedSource[] = [];
    const fail = (key: string, q: { refetch: () => unknown }) => failedSources.push({ key, refetch: () => q.refetch() });
    const windowEnd = addDaysISO(WINDOW_DAYS);

    if (canSeeHolidays) {
      if (holidaysQuery.data) {
        for (const h of holidaysQuery.data.items) {
          if (h.date >= todayISO() && h.date <= windowEnd) {
            events.push({ key: `holiday-${h.id}`, icon: "calendar", title: h.name, detail: "Holiday", date: h.date, tone: "amber" });
          }
        }
      } else if (holidaysQuery.isError) {
        fail("holidays", holidaysQuery);
      }
    }

    if (canSeeInterviews) {
      if (interviewsQuery.data) {
        for (const iv of interviewsQuery.data.items) {
          events.push({
            key: `interview-${iv.id}`,
            icon: "users",
            title: `Round ${iv.round_number} interview`,
            detail: iv.interview_type ? iv.interview_type.replace(/_/g, " ") : "Recruitment",
            date: iv.scheduled_date,
            tone: "indigo",
          });
        }
      } else if (interviewsQuery.isError) {
        fail("interviews", interviewsQuery);
      }
    }

    if (canSeePerformance) {
      if (performanceQuery.data) {
        for (const d of performanceQuery.data.upcoming_deadlines ?? []) {
          if (d.due_date >= todayISO() && d.due_date <= windowEnd) {
            events.push({
              key: `perf-${d.cycle_id}-${d.stage}`,
              icon: "target",
              title: `${d.cycle_name} - ${d.stage.replace(/_/g, " ")}`,
              detail: "Performance deadline",
              date: d.due_date,
              tone: "accent",
            });
          }
        }
      } else if (performanceQuery.isError) {
        fail("performance", performanceQuery);
      }
    }

    if (canSeeOnboarding) {
      if (onboardingQuery.data) {
        for (const o of onboardingQuery.data.items) {
          if (o.start_date >= todayISO() && o.start_date <= windowEnd) {
            events.push({
              key: `onboarding-${o.id}`,
              icon: "clipboard-list",
              title: "New hire starting",
              detail: "Onboarding",
              date: o.start_date,
              tone: "emerald",
            });
          }
        }
      } else if (onboardingQuery.isError) {
        fail("onboarding", onboardingQuery);
      }
    }

    if (canSeeBirthdays) {
      if (birthdaysQuery.data) {
        for (const ev of birthdaysQuery.data) {
          events.push({
            key: `${ev.event_type}-${ev.employee_id}`,
            icon: ev.event_type === "birthday" ? "gift" : "trending-up",
            title: ev.event_type === "birthday" ? `${ev.name}'s birthday` : `${ev.name}'s work anniversary`,
            detail: ev.event_type === "anniversary" && ev.years ? `${ev.years} year${ev.years === 1 ? "" : "s"}${ev.department ? ` · ${ev.department}` : ""}` : ev.department ?? "",
            date: ev.date,
            tone: "indigo",
          });
        }
      } else if (birthdaysQuery.isError) {
        fail("birthdays", birthdaysQuery);
      }
    }

    if (canSeeCompanyEvents) {
      if (companyEventsQuery.data) {
        for (const a of companyEventsQuery.data.items) {
          // An author also sees drafts in this list; only published events count.
          if (a.status !== "published" || !a.event_at) continue;
          const date = toBusinessDate(new Date(a.event_at));
          if (date >= todayISO() && date <= windowEnd) {
            events.push({ key: `event-${a.id}`, icon: "calendar", title: a.title, detail: a.location || "Company event", date, tone: "emerald" });
          }
        }
      } else if (companyEventsQuery.isError) {
        fail("company events", companyEventsQuery);
      }
    }

    events.sort((a, b) => a.date.localeCompare(b.date));
    return { events: events.slice(0, MAX_ITEMS), failedSources };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    holidaysQuery.data, holidaysQuery.isError, canSeeHolidays,
    interviewsQuery.data, interviewsQuery.isError, canSeeInterviews,
    performanceQuery.data, performanceQuery.isError, canSeePerformance,
    onboardingQuery.data, onboardingQuery.isError, canSeeOnboarding,
    birthdaysQuery.data, birthdaysQuery.isError, canSeeBirthdays,
    companyEventsQuery.data, companyEventsQuery.isError, canSeeCompanyEvents,
  ]);

  const isInitialLoading = [
    canSeeHolidays && holidaysQuery.isLoading,
    canSeeInterviews && interviewsQuery.isLoading,
    canSeePerformance && performanceQuery.isLoading,
    canSeeOnboarding && onboardingQuery.isLoading,
    canSeeBirthdays && birthdaysQuery.isLoading,
    canSeeCompanyEvents && companyEventsQuery.isLoading,
  ].some(Boolean);

  return (
    <Card interactive className={className}>
      <CardHeader>
        <CardTitle>Upcoming Events</CardTitle>
        {canAddEvent && (
          <Link to="/announcements/manage?new=event" className="text-xs font-medium text-primary-700 hover:underline" data-testid="add-upcoming-event">
            + Add event
          </Link>
        )}
      </CardHeader>
      <CardBody className={events.length > 0 ? "p-0" : undefined}>
        {isInitialLoading ? (
          <div className="space-y-3 p-5">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex items-center gap-3">
                <Skeleton className="h-8 w-8 rounded-lg" />
                <div className="flex-1 space-y-1.5">
                  <Skeleton className="h-3.5 w-2/3" />
                  <Skeleton className="h-3 w-1/4" />
                </div>
              </div>
            ))}
          </div>
        ) : events.length === 0 ? (
          <EmptyState icon={<Icon name="calendar" className="h-7 w-7" />} title="Nothing coming up" description="No events in the next 30 days." />
        ) : (
          <ul className="divide-y divide-slate-100">
            {events.map((event) => (
              <li key={event.key} className="flex items-center gap-3 px-5 py-3">
                <div className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-lg", TONE_CLASSES[event.tone])}>
                  <Icon name={event.icon} className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-slate-800">{event.title}</p>
                  <p className="truncate text-xs text-slate-500">
                    {event.detail ? `${event.detail} · ` : ""}
                    {formatDate(event.date)}
                  </p>
                </div>
                <span className="shrink-0 text-xs font-medium text-slate-400">{relativeDay(event.date)}</span>
              </li>
            ))}
          </ul>
        )}
        {failedSources.length > 0 && (
          <div className="flex items-center justify-between gap-3 border-t border-slate-100 bg-amber-50/50 px-5 py-2.5 text-xs text-amber-700">
            <span>Some events couldn't load ({failedSources.length}).</span>
            <button
              type="button"
              onClick={() => failedSources.forEach((f) => f.refetch())}
              className="font-medium underline hover:no-underline"
            >
              Retry
            </button>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
